/**
 * "What this means for flying today" (docs/specs/space-weather.md §4.3 A; decisions 623, 704, 719): one word, one
 * sentence, the reasons and what Thomson's history says for days like this, judged against the open project's own
 * Diurnal QC limits (Plot 1, and Plot 2 when the project has set its own Plot 2 limit and it is shown, decision 742;
 * `resolveDiurnalSettings`).
 *
 * The levels are MB's (decision 750) and live in ONE table, `OUTLOOK_THRESHOLDS`, one row per history rule (see its
 * comment for the rows). Each project limit is judged with the row of its nearest history rule (`historyRuleFor`),
 * and the verdict names the limit it is for. Beside the level the screen gives what Thomson's basemag history says
 * for days like this (`BASEMAG_HISTORY_AU`, space-weather-history.ts; docs/research/2026-10-27-basemag-history.md §3
 * and §6): Plot 1 and Plot 2 from MagStation and G-823 bases only, the bases tight specifications use (decision 749),
 * so Plot 2 assumes one and the screen says so. Thin bands (fewer than 30 station-days) are called indicative.
 * The screen never says "safe" and never says a flight passes: the basemag and Diurnal QC decide.
 */
import {
  BASEMAG_HISTORY_AU,
  HISTORY_INDICATIVE_BELOW,
  historyBandWords,
  historyRuleFor,
  kpBand,
  type HistoryRuleId,
  type KpBand,
} from './space-weather-history.js';
import type { ProjectOverrides } from './settings.js';

export type OutlookLevel = 'calm' | 'unsettled' | 'likely' | 'storm' | 'unknown';
export type OutlookTone = 'pass' | 'warn' | 'fail' | 'muted';

/** The values one level starts at (each "at or above"). */
export interface OutlookLevelThresholds {
  /** Planetary Kp (now, or forecast for the rest of today's daylight). */
  readonly kp: number;
  /** BoM K, Australian region or the nearest site (null: not used). */
  readonly kAus: number | null;
  /** Today's Ap so far (null: not used). */
  readonly ap: number | null;
  /** NOAA G level now, a NOAA watch for today, or a BoM magnetic alert's G scale. */
  readonly g: number;
}

/** One row: where Unsettled, Exceedances likely and Storm start, and whether Calm can be said at all. */
export interface OutlookLevelRow {
  readonly unsettled: OutlookLevelThresholds;
  readonly likely: OutlookLevelThresholds;
  /** Only conditions now (never a forecast) reach "Storm". */
  readonly storm: OutlookLevelThresholds;
  /** False: the lowest level is "Unsettled" (Plot 2 at 1.5 nT in 1 min; high latitude). */
  readonly calmPossible: boolean;
}

export interface OutlookThresholds {
  /** Where the high-latitude row replaces the others (absolute magnetic latitude, degrees). */
  readonly highLatitudeFromDeg: number;
  /** Mid latitudes (Australia), one row per history rule. */
  readonly rules: Readonly<Record<HistoryRuleId, OutlookLevelRow>>;
  readonly highLatitude: OutlookLevelRow;
  /** The nearest observatory's metric in the last hour, as fractions of the project's limit. */
  readonly observatory: { readonly unsettledFraction: number; readonly likelyFraction: number };
  /** Southward Bz held 30 minutes (nT, at or below) with solar wind at or above this speed (km/s): "Unsettled". */
  readonly solarWind: { readonly bzNt: number; readonly speedKmS: number };
  /** Hover text: where the numbers come from and that they are provisional. */
  readonly basis: string;
}

/**
 * MB's levels (decision 750, 27 Oct 2026), stricter than the history alone suggested, one row per history rule. A Kp
 * is compared as its whole number (Kp 2.67, "3-", is Kp 3), as the history's bands round it; BoM's K is a whole number;
 * NOAA's G scale starts at Kp 5 (G1), G2 at Kp 6. Ap (a whole day's average) moves a day up a level only where the
 * per-sensor history shows a step (research §6 "By daily Ap"): Ap 8 or more is Unsettled (the 8-15 band's share over
 * is the Kp 3 band's: 16% of chord days, 28% Plot 1, 69% Plot 2) and Ap 30 or more Exceedances likely (32%, 71% and
 * 100%); the 16-29 band is no worse than 8-15 on any rule, so it sets nothing; Ap never makes a Storm.
 *
 * - 10 nT from a 10-minute chord: Calm Kp 2 or less; Unsettled Kp 3 to 4; Exceedances likely Kp 5+; Storm Kp 6+ now.
 * - Plot 1 (7.5 nT in 5 min) and Plot 2 (1.5 nT in 1 min): Calm Kp 2 or less; Unsettled Kp 3; Exceedances likely
 *   Kp 4+; Storm Kp 5+ now. Plot 2's Calm still says that a few minutes over are common (38% of Kp 0-2 days).
 * - A base above 55° magnetic latitude: never Calm; Unsettled up to Kp 3; Exceedances likely Kp 4+; Storm Kp 5+ now.
 */
export const OUTLOOK_THRESHOLDS: OutlookThresholds = {
  highLatitudeFromDeg: 55,
  rules: {
    chord10: {
      unsettled: { kp: 3, kAus: 3, ap: 8, g: 0 },
      likely: { kp: 5, kAus: 5, ap: 30, g: 1 },
      storm: { kp: 6, kAus: 6, ap: null, g: 2 },
      calmPossible: true,
    },
    plot1Tight: {
      unsettled: { kp: 3, kAus: 3, ap: 8, g: 0 },
      likely: { kp: 4, kAus: 4, ap: 30, g: 1 },
      storm: { kp: 5, kAus: 5, ap: null, g: 1 },
      calmPossible: true,
    },
    plot2Tight: {
      unsettled: { kp: 3, kAus: 3, ap: 8, g: 0 },
      likely: { kp: 4, kAus: 4, ap: 30, g: 1 },
      storm: { kp: 5, kAus: 5, ap: null, g: 1 },
      calmPossible: true,
    },
  },
  highLatitude: {
    unsettled: { kp: 0, kAus: null, ap: 0, g: 0 },
    likely: { kp: 4, kAus: null, ap: 16, g: 1 },
    storm: { kp: 5, kAus: null, ap: null, g: 1 },
    calmPossible: false,
  },
  observatory: { unsettledFraction: 0.5, likelyFraction: 1 },
  solarWind: { bzNt: -5, speedKmS: 500 },
  basis:
    'Levels set by MB (decision 750, 27 Oct 2026). The words beside them come from Thomson’s basemag history: 407 Australian station-days for the 10 nT rule, and for Plot 1 and Plot 2 the 196 days of MagStation and G-823 bases only, the bases those specifications use (docs/research/2026-10-27-basemag-history.md, sections 3 and 6).',
};

// ---------------------------------------------------------------------------------------------- the project's limits

/**
 * One of the project's counting Diurnal QC limits: Plot 1 always; Plot 2 when the project has set its own Plot 2 limit
 * (decision 742) and it is shown, so it flags.
 */
export interface DiurnalLimit {
  readonly label: string;
  readonly mode: 'peakToPeak' | 'deviationFromChord';
  readonly windowMin: number;
  readonly thresholdNt: number;
}

/**
 * The project has set its own Plot 2 limit (decision 742): a Plot 2 threshold or window in the project's own Diurnal
 * QC settings (Project settings, `qc_overrides`). A Plot 2 left blank takes the company default and does not count.
 */
export function projectSetsPlot2(project: ProjectOverrides | null | undefined): boolean {
  const set = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v);
  return set(project?.plot2ThresholdNt) || set(project?.plot2WindowMin);
}

/**
 * The project's counting limits from its resolved Diurnal QC settings (`resolveDiurnalSettings(...).settings`): Plot 1
 * always, Plot 2 only when the project set its own Plot 2 limit (`ownPlot2`, `projectSetsPlot2`; decision 742) and it
 * is shown.
 */
export function diurnalLimits(
  settings: {
    readonly plotMode: 'peakToPeak' | 'deviationFromChord';
    readonly plot1WindowMin: number;
    readonly plot1ThresholdNt: number;
    readonly plot2WindowMin: number;
    readonly plot2ThresholdNt: number;
    readonly show: { readonly plot2: boolean };
  },
  ownPlot2: boolean,
): readonly DiurnalLimit[] {
  const out: DiurnalLimit[] = [
    {
      label: 'Plot 1',
      mode: settings.plotMode,
      windowMin: settings.plot1WindowMin,
      thresholdNt: settings.plot1ThresholdNt,
    },
  ];
  if (settings.show.plot2 && ownPlot2)
    out.push({
      label: 'Plot 2',
      mode: settings.plotMode,
      windowMin: settings.plot2WindowMin,
      thresholdNt: settings.plot2ThresholdNt,
    });
  return out;
}

const MODE_WORDS: Record<DiurnalLimit['mode'], string> = {
  peakToPeak: 'peak to peak',
  deviationFromChord: 'from the chord',
};

/** "Plot 2 peak to peak 1.5 nT in 1 min". */
export function limitWords(l: DiurnalLimit): string {
  return `${l.label} ${MODE_WORDS[l.mode]} ${l.thresholdNt} nT in ${l.windowMin} min`;
}

// ---------------------------------------------------------------------------------------------- the verdict

/** What the verdict is worked out from; null where a source has not answered. */
export interface OutlookInput {
  readonly nowMs: number;
  /** The newest fetch of any K or Kp answer (the laptop's copy), ms; null when none was ever held. */
  readonly newestFetchMs: number | null;
  /** Magnetic latitude of the blocks' centroid (`mltWindows(...).magLatDeg`); null: treated as mid-latitude. */
  readonly magLatDeg: number | null;
  readonly kAus: number | null;
  /** The nearest BoM K site's newest K, with its name. */
  readonly kNearest: { readonly site: string; readonly k: number } | null;
  /** Planetary Kp now (the 1-minute estimate, else the newest 3-hour value). */
  readonly kpNow: number | null;
  /** The highest forecast Kp over the rest of today's daylight. */
  readonly kpDaylight: number | null;
  readonly apToday: number | null;
  readonly noaaGNow: number | null;
  /** The highest G of a current NOAA watch for today (UTC), 0 when the newest watch says none. */
  readonly noaaWatchToday: number | null;
  /** The current BoM magnetic alert's G scale. */
  readonly bomAlertG: number | null;
  /** A BoM geophysical warning covers today. */
  readonly bomWarningToday: boolean;
  /** The project's counting Diurnal QC limits (`diurnalLimits`), Plot 1 first. */
  readonly limits: readonly DiurnalLimit[];
  /**
   * The nearest observatory's highest value in the last hour of each limit's metric (same order as `limits`), nT,
   * and its code.
   */
  readonly observatory: { readonly code: string; readonly maxNt: readonly number[] } | null;
  /** The lowest 30-minute mean Bz of the last hour and the speed then. */
  readonly southwardBz: { readonly bz: number; readonly speed: number | null } | null;
}

/** What the history says for one limit on a day like this. */
export interface OutlookExpected {
  readonly limit: DiurnalLimit;
  readonly rule: HistoryRuleId;
  readonly band: KpBand;
  readonly stationDays: number;
  readonly shareOver: number;
  readonly medianMinOver: number;
  readonly medianDayLostPct: number;
  readonly words: string;
}

export interface Outlook {
  readonly level: OutlookLevel;
  readonly word: string;
  readonly tone: OutlookTone;
  readonly sentence: string;
  /** The facts that set the level first, then the rest, as short lines. */
  readonly reasons: readonly string[];
  readonly highLatitude: boolean;
  /** The limit the verdict is for (the one that sets the level; the tightest on a tie). */
  readonly forLimit: DiurnalLimit | null;
  /** What the history says for each limit at today's Kp band (null band: no Kp held). */
  readonly expected: readonly OutlookExpected[];
  /** Sample size, latitude and instrument notes. */
  readonly notes: readonly string[];
  /** Which limits the verdict used, in words ("Judged for your …"). */
  readonly limitsText: string;
  /** Hover text: which limits the verdict used, and the basis of the table. */
  readonly basis: string;
}

const WORD: Record<OutlookLevel, string> = {
  calm: 'Calm',
  unsettled: 'Unsettled',
  likely: 'Exceedances likely',
  storm: 'Storm',
  unknown: 'Unknown',
};
const TONE: Record<OutlookLevel, OutlookTone> = {
  calm: 'pass',
  unsettled: 'warn',
  likely: 'warn',
  storm: 'fail',
  unknown: 'muted',
};
type Known = Exclude<OutlookLevel, 'unknown'>;
const RANK: Record<Known, number> = { calm: 0, unsettled: 1, likely: 2, storm: 3 };
const TIGHT: Record<HistoryRuleId, number> = { chord10: 0, plot1Tight: 1, plot2Tight: 2 };

const fmt = (n: number, d = 0): string => (Number.isInteger(n) && d === 0 ? String(n) : n.toFixed(d || 2));

/** The level one limit reaches, with the reasons that raised it. */
function judge(
  input: OutlookInput,
  row: OutlookLevelRow,
  limit: DiurnalLimit | null,
  index: number,
  t: OutlookThresholds,
): { level: Known; why: { level: Known; text: string }[] } {
  let level: Known = 'calm';
  const why: { level: Known; text: string }[] = [];
  const raise = (l: Known, text: string): void => {
    why.push({ level: l, text });
    if (RANK[l] > RANK[level]) level = l;
  };
  const kAny = Math.max(input.kAus ?? -1, input.kNearest?.k ?? -1);
  // a Kp counts as its whole number, as the history's bands round it (Kp 2.67, "3-", is Kp 3)
  const kpNow = input.kpNow === null ? null : Math.round(input.kpNow);
  const kpDay = input.kpDaylight === null ? null : Math.round(input.kpDaylight);
  const g = Math.max(input.noaaGNow ?? 0, input.bomAlertG ?? 0);
  const check = (l: 'unsettled' | 'likely' | 'storm', th: OutlookLevelThresholds): void => {
    // a zero threshold is a floor, raised below with its own words
    if (kpNow !== null && th.kp > 0 && kpNow >= th.kp) raise(l, `Planetary Kp ${fmt(input.kpNow!)} now`);
    if (th.kAus !== null && th.kAus > 0 && kAny >= th.kAus) {
      const atSite = input.kNearest && input.kNearest.k === kAny && (input.kAus ?? -1) < kAny;
      raise(l, `BoM K ${kAny} now${atSite ? ` at ${input.kNearest!.site}` : ''}`);
    }
    if (th.g > 0 && g >= th.g)
      raise(
        l,
        input.bomAlertG !== null && input.bomAlertG >= th.g
          ? `BoM magnetic alert G${input.bomAlertG}`
          : `NOAA storm level G${g} now`,
      );
    if (l === 'storm') return; // only conditions now make a storm
    if (kpDay !== null && th.kp > 0 && kpDay >= th.kp)
      raise(l, `Kp ${fmt(input.kpDaylight!)} forecast in today’s daylight`);
    if (th.ap !== null && th.ap > 0 && input.apToday !== null && input.apToday >= th.ap)
      raise(l, `Ap ${input.apToday} so far today`);
    if (th.g > 0 && input.noaaWatchToday !== null && input.noaaWatchToday >= th.g)
      raise(l, `NOAA G${input.noaaWatchToday} watch for today`);
  };
  check('unsettled', row.unsettled);
  check('likely', row.likely);
  check('storm', row.storm);
  if (input.bomWarningToday) raise('unsettled', 'BoM geophysical warning for today');
  const sw = t.solarWind;
  const bz = input.southwardBz;
  if (bz && bz.bz <= sw.bzNt && bz.speed !== null && bz.speed >= sw.speedKmS)
    raise('unsettled', `Bz ${fmt(bz.bz, 1)} nT for 30 min at ${Math.round(bz.speed)} km/s`);
  const v = input.observatory?.maxNt[index];
  if (limit && input.observatory && v !== undefined && Number.isFinite(v) && limit.thresholdNt > 0) {
    const f = v / limit.thresholdNt;
    if (f > t.observatory.likelyFraction)
      raise(
        'likely',
        `${input.observatory.code} observatory ${fmt(v, 1)} nT in the last hour, over your ${limit.label} limit of ${limit.thresholdNt} nT`,
      );
    else if (f > t.observatory.unsettledFraction)
      raise(
        'unsettled',
        `${input.observatory.code} observatory ${fmt(v, 1)} nT in the last hour, over half your ${limit.label} limit`,
      );
  }
  if (!row.calmPossible && level === 'calm') {
    level = 'unsettled';
    why.push({
      level,
      text: limit ? `Your ${limitWords(limit)} goes over on many quiet days` : 'High magnetic latitude',
    });
  }
  return { level, why };
}

const ONE_IN = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

/** "About one day in ten like this" from a share over (0.10), or the share itself when it is a third or more. */
function oneDayIn(share: number): string {
  const n = share > 0 ? Math.round(1 / share) : 0;
  if (share >= 1 / 3 || n < 2) return `About ${Math.round(share * 100)}% of days like this`;
  return `About one day in ${n <= 10 ? ONE_IN[n] : n} like this`;
}

function sentence(
  level: OutlookLevel,
  forLimit: DiurnalLimit | null,
  rule: HistoryRuleId | null,
  high: boolean,
  exp: OutlookExpected | null,
): string {
  const lim = forLimit ? `your ${limitWords(forLimit)}` : 'your limits';
  const Lim = `${lim[0]!.toUpperCase()}${lim.slice(1)}`;
  const minutes = exp
    ? `expect about ${exp.medianMinOver} min over (${exp.medianDayLostPct.toFixed(1)}% of the day)`
    : '';
  switch (level) {
    case 'calm':
      // Plot 2's Calm keeps its note: a few minutes over are still common (decision 750)
      if (rule === 'plot2Tight')
        return `Space weather should not push the base station over ${lim} for long, but a few minutes over are still common${exp ? `: ${Math.round(exp.shareOver * 100)}% of days like this went over; ${minutes}` : ''}. Watch Diurnal QC.`;
      return `Space weather should not push the base station over ${lim}. ${exp ? oneDayIn(exp.shareOver) : 'About one quiet day in ten'} still goes over for a few minutes from local causes.`;
    case 'unsettled':
      if (high)
        return 'At this magnetic latitude the field is often disturbed even when Kp is low; watch the base station and Diurnal QC.';
      if (rule === 'plot2Tight')
        return `${Lim} went over on ${exp ? `${Math.round(exp.shareOver * 100)}% of` : 'most'} days like this${exp ? `; ${minutes}` : ''}. Watch Diurnal QC.`;
      return 'Expect some disturbed periods; watch the base station and Diurnal QC.';
    case 'likely':
      return `The base station is likely to break ${lim} in places; lines may need reflying.`;
    case 'storm':
      return `A strong geomagnetic storm is under way: on days like this most base stations went over ${lim}; check every line in Diurnal QC.`;
    case 'unknown':
      return 'Space weather data are too old to judge.';
  }
}

/**
 * Where the verdict's "Storm" starts for a project's counting limits, as a BoM K and a G scale (decision 750): the
 * lowest of its limits' Storm rows (a high-latitude base takes its own row, whose Kp stands in for the K). The storm
 * notices' "Storm now" uses it (`stormAlertEvents` `stormFrom`), so the screen and the notices agree.
 */
export function stormNowFrom(
  limits: readonly DiurnalLimit[],
  magLatDeg: number | null,
  thresholds: OutlookThresholds = OUTLOOK_THRESHOLDS,
): { readonly kAus: number; readonly g: number } {
  const high = magLatDeg !== null && Math.abs(magLatDeg) >= thresholds.highLatitudeFromDeg;
  const rows = high
    ? [thresholds.highLatitude]
    : (limits.length ? limits.map(historyRuleFor) : (['chord10'] as const)).map((r) => thresholds.rules[r]);
  return {
    kAus: Math.min(...rows.map((r) => r.storm.kAus ?? r.storm.kp)),
    g: Math.min(...rows.map((r) => r.storm.g)),
  };
}

/**
 * The verdict. Each counting limit is judged with its history rule's row; the highest level wins and names its
 * limit. "Unknown" when the newest K or Kp copy is over 3 hours old or nothing was ever held (spec §4.5). Pure.
 */
export function spaceWeatherOutlook(
  input: OutlookInput,
  thresholds: OutlookThresholds = OUTLOOK_THRESHOLDS,
  unknownAfterMs = 3 * 3_600_000,
): Outlook {
  const high = input.magLatDeg !== null && Math.abs(input.magLatDeg) >= thresholds.highLatitudeFromDeg;
  const limits = input.limits;
  const rules = limits.map(historyRuleFor);
  const used = limits.length
    ? `Judged for your ${limits.map((l, i) => `${limitWords(l)} (history rule: ${BASEMAG_HISTORY_AU[rules[i]!].label})`).join('; ')}${high ? ', at high magnetic latitude' : ''}.`
    : 'No diurnal limits set: judged with the 10 nT 10-minute chord rule.';
  const basis = `${used} ${thresholds.basis}`;
  // what the history says at today's band (the higher of Kp now and the daylight forecast)
  const kpRef = Math.max(input.kpNow ?? -1, input.kpDaylight ?? -1);
  const band = kpRef >= 0 ? kpBand(kpRef) : null;
  const expected: OutlookExpected[] = band
    ? limits.map((limit, i) => {
        const rule = BASEMAG_HISTORY_AU[rules[i]!];
        const h = rule.byKp[band];
        return {
          limit,
          rule: rule.id,
          band,
          stationDays: h.stationDays,
          shareOver: h.shareOver,
          medianMinOver: h.medianMinOver,
          medianDayLostPct: h.medianDayLostPct,
          words: `${limit.label}: ${historyBandWords(rule, band)}`,
        };
      })
    : [];
  const notes: string[] = [];
  // the sample: a thin band of the history is indicative (the tight rules have 13 days at Kp 5 and 9 at Kp 6+)
  const thin = expected.filter((e) => e.stationDays < HISTORY_INDICATIVE_BELOW);
  if (band && thin.length) {
    const fewest = thin.reduce((a, e) => (e.stationDays < a.stationDays ? e : a));
    const bases = BASEMAG_HISTORY_AU[fewest.rule].sensors.includes('g857') ? '' : 'MagStation and G-823 ';
    notes.push(
      `Only ${fewest.stationDays} ${bases}station-days at Kp ${band} in the history: this band is indicative.`,
    );
  }
  if (input.apToday !== null && input.apToday >= 50)
    notes.push(
      `Ap ${input.apToday}: only ${BASEMAG_HISTORY_AU.chord10.byAp['50+'].stationDays} station-days at Ap 50 or more in the history (${BASEMAG_HISTORY_AU.plot2Tight.byAp['50+'].stationDays} on MagStation and G-823 bases): indicative.`,
    );
  // Plot 2 needs a MagStation or G-823 base (decision 749); 857-format files do not say whether a G-823 or a G-857
  // made them, so the screen says what it assumes
  if (rules.includes('plot2Tight'))
    notes.push(
      'Plot 2 assumes a MagStation or G-823 base station, the bases used for Plot 2 specifications (the history behind it is those bases only).',
    );
  notes.push(
    high
      ? 'Planetary Kp fits high latitudes poorly (Mayo, Yukon went over every limit for hours at Kp 4 to 5): a rough guide only.'
      : 'Planetary Kp fits Australia only roughly; the history is Australian bases.',
  );
  if (input.newestFetchMs === null || input.nowMs - input.newestFetchMs > unknownAfterMs) {
    return {
      level: 'unknown',
      word: WORD.unknown,
      tone: TONE.unknown,
      sentence: sentence('unknown', null, null, high, null),
      reasons:
        input.newestFetchMs === null
          ? ['No space weather source has answered on this laptop yet.']
          : ['The newest K and Kp on this laptop are more than 3 hours old.'],
      highLatitude: high,
      forLimit: null,
      expected,
      notes,
      limitsText: used,
      basis,
    };
  }
  const judged = (limits.length ? limits : [null]).map((limit, i) => {
    const rule: HistoryRuleId = limit ? rules[i]! : 'chord10';
    const row = high ? thresholds.highLatitude : thresholds.rules[rule];
    return { limit, rule, ...judge(input, row, limit, i, thresholds) };
  });
  let best = judged[0]!;
  for (const j of judged.slice(1))
    if (
      RANK[j.level] > RANK[best.level] ||
      (RANK[j.level] === RANK[best.level] && TIGHT[j.rule] > TIGHT[best.rule])
    )
      best = j;
  const level = best.level;
  const lead = best.why.filter((w) => w.level === level).map((w) => w.text);
  const context: string[] = [];
  if (input.kAus !== null && !lead.some((l) => l.startsWith('BoM K')))
    context.push(`Australian K ${input.kAus}`);
  if (input.kNearest && !lead.some((l) => l.includes(input.kNearest!.site)))
    context.push(`${input.kNearest.site} K ${input.kNearest.k}`);
  if (input.kpNow !== null && !lead.some((l) => l.startsWith('Planetary Kp')))
    context.push(`Planetary Kp ${fmt(input.kpNow)}`);
  if (input.kpDaylight !== null && !lead.some((l) => l.includes('daylight')))
    context.push(`Kp forecast for today’s daylight at most ${fmt(input.kpDaylight)}`);
  if (!input.bomWarningToday && input.bomAlertG === null) context.push('No BoM warning or alert');
  limits.forEach((l, i) => {
    const v = input.observatory?.maxNt[i];
    if (
      input.observatory &&
      v !== undefined &&
      Number.isFinite(v) &&
      !lead.some((x) => x.includes(`your ${l.label}`))
    )
      context.push(
        `${input.observatory.code} observatory at most ${fmt(v, 1)} nT in the last hour (${l.label})`,
      );
  });
  const forExp = expected.find((e) => e.limit === best.limit) ?? null;
  return {
    level,
    word: WORD[level],
    tone: TONE[level],
    sentence: sentence(level, best.limit, best.limit ? best.rule : null, high, forExp),
    reasons: [...new Set([...lead, ...context])],
    highLatitude: high,
    forLimit: best.limit,
    expected,
    notes,
    limitsText: used,
    basis,
  };
}

/** Bands with fewer station-days than this are called indicative (re-exported for the screen). */
export const OUTLOOK_INDICATIVE_BELOW = HISTORY_INDICATIVE_BELOW;
